import { createFeatureSelector, createSelector } from '@ngrx/store';
import { format } from 'date-fns';
import { StudyFile } from 'src/app/models/Study/study-file.model';
import {
  SaleHorseStudyBase,
  StudyDicomInfo,
} from 'src/app/models/Study/study.model';
import { MediaCategory } from 'src/app/models/media/media-category.model';
import { MediaGroup } from 'src/app/models/media/media-group.model';
import { SaleHorseNoteModel } from '../../../models/sales/sale-horse-note.model';
import { SaleHorse } from '../../../models/sales/sale-horse.model';
import { DateParserFormatter } from '../../../utils/helper/date-parser-formatter';
import { StudyState } from './studies.reducer';
import { StudyStatus } from '../../enums/study-status.enum';
import { XrayCompleteType } from '../../enums/xray-complete-type.enum';
import { SubmissionValidationIssue } from 'src/app/models/Study/submission-validation-issue.model';

export const selectState = createFeatureSelector<StudyState>('studies');
const inactiveMediaGroups = ['unknown', 'additional'];


export const selectIsLoading = createSelector(
  selectState,
  (state: StudyState) => {
    return state.isLoading;
  }
);

export const selectStudies = createSelector(
  selectState,
  (state: StudyState) => {
    return state.studies;
  }
);

export const selectStudiesLoaded = createSelector(
  selectState,
  (state: StudyState) => state.studiesLoaded
);

export const selectStudiesError = createSelector(
  selectState,
  (state: StudyState) => state.studyListError
);

export const selectActiveStudy = createSelector(
  selectState,
  (state: StudyState) => {
    return state.study;
  }
);

export const selectActiveStudyProfile = createSelector(
  selectState,
  (state: StudyState) => {
    return state.studyProfile;
  }
);

export const selectSaleSettings = createSelector(
  selectState,
  (state: StudyState) => {
    return state.saleSettings;
  }
);

export const selectActiveStudyStatusId = createSelector(
  selectActiveStudy,
  (study: SaleHorseStudyBase): number => {
    if (
      study?.studyFiles.some(
        (sf) => sf.studyStatusId === 1 || !sf.studyStatusId
      )
    ) {
      return 1;
    } else if (
      study?.studyFiles.some(
        (sf) => sf.studyStatusId === 3 || sf.studyStatusId === 2
      )
    ) {
      return 3;
    } else {
      return 4;
    }
  }
);

export const selectActiveStudyLoaded = createSelector(
  selectState,
  (state: StudyState) => state.studyLoaded
);

export const selectActiveStudyStatus = createSelector(
  selectState,
  (state: StudyState) => {
    return state.study?.studyStatus;
  }
);

export const selectStatuses = createSelector(
  selectState,
  (state: StudyState) => state.statuses
);

export const selectStudyStatusError = createSelector(
  selectState,
  (state: StudyState) => state.studyStatusError
);
export const selectStudyStatusSuccess = createSelector(
  selectState,
  (state: StudyState) => state.studyStatusSuccess
);

export const selectStudyStatusUpdated = createSelector(
  selectState,
  (state: StudyState) => state.isStatusUpdated
);

export const selectMediaGroupState = createSelector(
  selectState,
  selectActiveStudy,
  (state: StudyState, study) => {
    if (!state.saleSettings) {
      return [];
    }
    return state.saleSettings.mediaGroups
      .filter(
        (mediaGroup) =>
          !inactiveMediaGroups.includes(mediaGroup.name.toLowerCase())
      )
      .map((mediaGroup) => {
        return {
          ...mediaGroup,
          mediaCategories: mediaGroup.mediaCategories.map((category) => {
            const studyFile = study?.studyFiles.find(
              (sf) => sf.mediaCategoryId === category.id && !sf.isAdditional
            );
            return {
              ...category,
              matched: !!studyFile,
              assignedFileName: studyFile ? studyFile?.categoryName : '',
              mediaGroupName: mediaGroup.name,
            } as MediaCategory;
          }),
        };
      })
      .map((mediaGroup) => {
        return {
          ...mediaGroup,
          hasEmptyCategories: !mediaGroup.mediaCategories.find(
            (mc) => !mc.matched
          ),
        } as MediaGroup;
      });
  }
);

export const selectMediaGroups = createSelector(
  selectMediaGroupState,
  (mediaGroups) => {
    return mediaGroups.filter(
      (mediaGroup) =>
        !inactiveMediaGroups.includes(mediaGroup.name.toLowerCase())
    );
  }
);

export const selectMediaGroupsAll = createSelector(
  selectState,
  selectActiveStudy,
  (state: StudyState, study) => {
    if (!state.mediaGroups) {
      return [];
    }
    return state.mediaGroups
      .filter(
        (mediaGroup) =>
          !inactiveMediaGroups.includes(mediaGroup.name.toLowerCase())
      )
      .map((mediaGroup) => {
        return {
          ...mediaGroup,
          mediaCategories: mediaGroup.mediaCategories.map((category) => {
            const studyFile = study?.studyFiles.find(
              (sf) => sf.mediaCategoryId === category.id && !sf.isAdditional && (sf.studyStatusId == StudyStatus.Draft || sf.studyStatusId == StudyStatus.Submitted)
            );
            const studyFiles = study?.studyFiles.filter(
              (sf) => sf.mediaCategoryId === category.id && !sf.isAdditional && (sf.studyStatusId == StudyStatus.Draft || sf.studyStatusId == StudyStatus.Submitted)
            );
            return {
              ...category,
              matched: !!studyFile,
              isShowReplace: false,
              isShowAssign: false,
              matchedStudyFileIds: studyFiles?.map((studyFile) => studyFile.studyFileId),
              assignedFileName: studyFile ? studyFile?.categoryName : '',
              mediaGroupName: mediaGroup.name,
            } as MediaCategory;
          }),
        };
      })
      .map((mediaGroup) => {
        return {
          ...mediaGroup,
          hasEmptyCategories: !mediaGroup.mediaCategories.find(
            (mc) => !mc.matched
          ),
        } as MediaGroup;
      });
  }
);

export const selectMediaCategories = createSelector(
  selectMediaGroups,
  (mediaGroups) => {
    let mediaCategories: MediaCategory[] = [];
    mediaGroups
      .map((mg) => {
        return mg.mediaCategories.flat();
      })
      .forEach((mc) => {
        mediaCategories = [...mediaCategories, ...mc];
      });

    return mediaCategories;
  }
);

export const selectRawMediaCategories = createSelector(
  selectMediaGroupsAll,
  (mediaGroups) => {
    let mediaCategories: MediaCategory[] = [];
    mediaGroups
      .map((mg) => {
        return mg.mediaCategories.flat();
      })
      .forEach((mc) => {
        mediaCategories = [...mediaCategories, ...mc];
      });

    return mediaCategories;
  }
);

export const selectMissingMediaCategories = createSelector(
  selectMediaCategories,
  (mediaCategories) => {
    return mediaCategories.filter((mc) => !mc.matched);
  }
);

export const selectStudyFiles = createSelector(
  selectState,
  (state: StudyState) => state.study?.studyFiles
);

export const selectStudyFilesToUpload = createSelector(
  selectStudyFiles,
  (studyFiles: StudyFile[]) =>
    studyFiles?.filter((sf) => sf.studyStatusId === 1) || []
);

export const selectSharedStudyFiles = createSelector(
  selectState,
  (state: StudyState) => state.sharedStudyFiles
);

function computeMediaGroupXrays(xrayStudyFiles: StudyFile[], mediaGroups: MediaGroup[], mediaCategories: MediaCategory[], rawMediaCategories: MediaCategory[]) {

  const filesMediaCategories: MediaCategory[] = [];

  xrayStudyFiles.forEach((studyFile) => {
    const category = rawMediaCategories.find(
      category => category.id === studyFile.mediaCategoryId
    );
    if (category) filesMediaCategories.push(category);
  });

  const combined = [
    ...filesMediaCategories.map(item => ({ ...item, isRequired: false })),
    ...mediaCategories.map(item => ({ ...item, isRequired: true })),
  ];

  const displayMediaCategories = Array.from(
    new Map(combined.map(item => [item.id, item])).values()
  );

  const matchingStudyFiles = xrayStudyFiles.filter(sf =>
    mediaCategories.some(mc => mc.id === sf.mediaCategoryId)
  );
  const distinctCategoryIds = new Set(matchingStudyFiles.map(sf => sf.mediaCategoryId));

  const displayMediaGroups = structuredClone(mediaGroups)
    .map((mediaGroup: MediaGroup) => {
      mediaGroup.requiredMediaCount = mediaCategories.filter(mc => mc.mediaGroupId === mediaGroup.id).length;
      mediaGroup.uploadXrayMediaCount = mediaCategories.filter(x => x.mediaGroupId === mediaGroup.id && distinctCategoryIds.has(x.id)).length;

      mediaGroup.mediaCategories = displayMediaCategories
        .filter(x => x.mediaGroupId === mediaGroup.id)
        .map((mediaCategory: MediaCategory) => {
          mediaCategory.studyFiles = structuredClone(xrayStudyFiles)
            .filter(x => x.mediaCategoryId === mediaCategory.id);
          return mediaCategory;
        });
      return mediaGroup;
    })
    //.filter(group => group.mediaCategories.length > 0);

  const distinctCategoryIdsArray = Array.from(
    new Set(matchingStudyFiles.map(sf => sf.mediaCategoryId))
  );
  const uploadXrayCount = distinctCategoryIdsArray.length;
  const requiredCategoriesCount = mediaCategories.length;

  return {
    requiredCategoriesCount,
    displayMediaGroups,
    uploadXrayCount
  };
}

export const selectXrays = (
  loggedInMemberId: number,
  studyStatusId: number = StudyStatus.Draft
) =>
  createSelector(
    selectActiveStudy,
    selectMediaGroupsAll,
    selectMediaCategories,
    selectRawMediaCategories,
    (
      study,
      mediaGroups,
      mediaCategories,
      rawMediaCategories
    ) => {
      const xrayStudyFiles: StudyFile[] =
        study?.studyFiles?.filter(
          (sf) =>
            sf.studyStatusId === studyStatusId &&
            sf.studyFileMimeTypeId !== 1 &&
            sf.studyFileMimeTypeId !== 2 &&
            !sf.isAdditional &&
            sf.memberId === loggedInMemberId
        ) || [];

      // Now you can pass all the values to your helper
      return computeMediaGroupXrays(
        xrayStudyFiles,
        mediaGroups,
        mediaCategories,
        rawMediaCategories
      );
    }
  );

export const selectXraysForPreview = (
  studyFiles: StudyFile[]
) => createSelector(
  selectMediaGroupsAll,
  selectMediaCategories,
  selectRawMediaCategories,
  (mediaGroups,
    mediaCategories,
    rawMediaCategories) => {
    return computeMediaGroupXrays(studyFiles, mediaGroups,
      mediaCategories,
      rawMediaCategories);
  }
);


export const selectStudyFileMap = (isDraftOnly: boolean = false) =>
  createSelector(
    selectActiveStudy,
    selectMediaGroups,
    selectMediaCategories,
    (study, mediaGroups, mediaCategories) => {
      const mp4StudyFiles: StudyFile[] = [];
      const pdfStudyFiles: StudyFile[] = [];
      const additionalStudyFiles: StudyFile[] = [];
      const unmatchedStudyFiles: StudyFile[] = [];
      const unknownStudyFiles: StudyFile[] = [];
      const xrayAllStudyFiles: StudyFile[] = [];
      const uploadStudyFiles: StudyFile[] = [];
      const inReviewStudyFiles: StudyFile[] = [];
      const deniedStudyFiles: StudyFile[] = [];
      const approvedStudyFiles: StudyFile[] = [];
      const xrayStudyFiles: StudyFile[] = [];
      study?.studyFiles
        // Filter out denied studies
        .filter((sf) =>
          !!isDraftOnly ? sf.studyStatusId === 1 : sf.studyStatusId !== 3
        )
        .forEach((studyFile) => {
          if (studyFile.studyFileMimeTypeId === 1) {
            mp4StudyFiles.push(studyFile);
          } else if (studyFile.studyFileMimeTypeId === 2) {
            pdfStudyFiles.push(studyFile);
          } else if (studyFile.isAdditional) {
            additionalStudyFiles.push(studyFile);
          } else {
            if (studyFile.mediaCategoryId == null) {
              unmatchedStudyFiles.push(studyFile);
            }
            xrayStudyFiles.push(studyFile);
          }
        });

      xrayStudyFiles.forEach((studyFile) => {

        // Include all matched files in matchedStudyFiles, regardless of duplicates
        xrayAllStudyFiles.push(studyFile);
      });

      [...xrayAllStudyFiles, ...additionalStudyFiles].forEach((studyFile) => {
        switch (studyFile.studyStatusId) {
          case 1:
            uploadStudyFiles.push(studyFile);
            break;
          case 2:
            inReviewStudyFiles.push(studyFile);
            break;
          case 3:
            deniedStudyFiles.push(studyFile);
            break;
          case 4:
            approvedStudyFiles.push(studyFile);
            break;
          default:
            uploadStudyFiles.push(studyFile);
            break;
        }
      });

      return {
        mp4StudyFiles,
        pdfStudyFiles,
        additionalStudyFiles,
        unmatchedStudyFiles: [...unmatchedStudyFiles, ...unknownStudyFiles],
        unknownStudyFiles,
        xrayAllStudyFiles,
        uploadStudyFiles,
        inReviewStudyFiles,
        deniedStudyFiles,
        approvedStudyFiles,
        study,
      } as {
        mp4StudyFiles: StudyFile[];
        pdfStudyFiles: StudyFile[];
        additionalStudyFiles: StudyFile[];
        unmatchedStudyFiles: StudyFile[];
        unknownStudyFiles: StudyFile[];
        xrayAllStudyFiles: StudyFile[];
        uploadStudyFiles: StudyFile[];
        inReviewStudyFiles: StudyFile[];
        deniedStudyFiles: StudyFile[];
        approvedStudyFiles: StudyFile[];
        study: SaleHorseStudyBase;
      };
    }
  );


export const selectTabDisplayForEditStudy = createSelector(
  selectActiveStudy,
  (study) => {
    // Show upload tab
    const landOnApprovedTab = false;
    const landOnReviewTab = study?.isSalesCompany ? true : false;
    const landOnUploadTab = study?.isSalesCompany ? false : true
    return {
      landOnApprovedTab,
      landOnReviewTab,
      landOnUploadTab,
      loaded: !!study?.saleHorse,
    };
  }
);

// Upload screen only
export const selectMp4StudyFiles = createSelector(
  selectStudyFileMap(true),
  (studyFileMap) => studyFileMap.mp4StudyFiles
);

// Upload screen only
export const selectPdfStudyFiles = createSelector(
  selectStudyFileMap(true),
  (studyFileMap) => studyFileMap.pdfStudyFiles
);

// Upload screen only
export const selectAdditionalStudyFiles = createSelector(
  selectStudyFileMap(true),
  (studyFileMap) => studyFileMap.additionalStudyFiles
);

// Upload screen only
export const selectUnmatchedStudyFiles = createSelector(
  selectStudyFileMap(true),
  (studyFileMap) => studyFileMap.unmatchedStudyFiles
);

// Used by study profile and upload study
export const selectxRayAllStudyFiles = (isUploadOnly: boolean = false) =>
  createSelector(
    selectStudyFileMap(isUploadOnly),
    (studyFileMap) => studyFileMap.xrayAllStudyFiles
  );

// Upload screen only
//export const selectMultipleStudyFiles = createSelector(
//  selectStudyFileMap(true),
//  (studyFileMap) => studyFileMap.multipleStudyFiles
//);

export const selectStudyFileRows = createSelector(
  selectActiveStudy,
  (study) => {
    let horseInfo = {
      ...study?.saleHorse,
    };

    const dicomStudiesInfo = study?.saleHorseStudies || [];

    let horses: StudyDicomInfo[] = [];
    horses = dicomStudiesInfo?.map((study) => {
      let horse: SaleHorse = new SaleHorse();
      horse.accessionCode = study.dicomPatientId;
      // horse.lot = study.dicomPatientId;
      horse.name = study.patientName;
      horse.horseAgeCategoryName = study.patientAge;
      horse.studyDate = study?.studyDateTime
        ? format(
          new Date(study.studyDateTime),
          DateParserFormatter.getDateFormatForSale(study?.sale, true)
        )
        : '';
      horse.microchip = study.microchipNumber;
      horse.consignor = study.responsiblePerson;
      let studyFileDicom: StudyDicomInfo = {
        type: 'Upload',
        horse: horse,
        isAfterAllowedSubmissionDate: study.isAfterAllowedSubmissionDate,
      };
      return studyFileDicom;
    });
    if (horses) {
      if (horseInfo.studyDate) {
        horseInfo.studyDate = format(
          new Date(horseInfo.studyDate),
          DateParserFormatter.getDateFormatForSale(study?.sale, true)
        );
      }
      horses.push({
        horse: horseInfo,
        type: 'Repo',
        isAfterAllowedSubmissionDate: true,
      });
      let sortedHorses = horses.sort((a, b) => a.type.localeCompare(b.type));
      if (horseInfo.studyDate) {
        sortedHorses[0].horse.studyDate = format(
          new Date(horseInfo.studyDate),
          DateParserFormatter.getDateFormatForSale(study?.sale, true)
        );
      }
      return sortedHorses;
    }
    return null;
  }
);

// export const selectStudyFilesByTypeAndStatus = createSelector(
//   selectActiveStudy,
//   (study: Study) => {
//     const mp4StudyFiles: StudyFile[] = [];
//     const pdfStudyFiles: StudyFile[] = [];
//     const xrayStudyFiles: StudyFile[] = [];

//     // First, let's split up the studyFile collections.
//     study?.studyFiles.forEach((studyFile) => {
//       if (studyFile.originalFileName.includes('.mp4')) {
//         mp4StudyFiles.push(studyFile);
//       } else if (studyFile.originalFileName.includes('.pdf')) {
//         pdfStudyFiles.push(studyFile);
//       } else {
//         xrayStudyFiles.push(studyFile);
//       }
//     });

//     return {
//       mp4StudyFiles,
//       pdfStudyFiles,
//       xrayStudyFiles,
//     };
//     // Second, let's organise the studyFiles into groups based on the status update date.
//   }
// );

export const selectStudyFilesByTypeAndStatus = (statusId?: number) =>
  createSelector(selectActiveStudy, (study: SaleHorseStudyBase) => {
    let studyFiles = study?.studyFiles || [];
    if (statusId) {
      studyFiles = studyFiles.filter((sf) => sf.studyStatusId === statusId);
    } else {
      studyFiles = studyFiles.filter((sf) => sf.studyStatusId > 1);
    }
    const mp4StudyFiles: StudyFile[] = [];
    const pdfStudyFiles: StudyFile[] = [];
    const xrayStudyFiles: StudyFile[] = [];
    const saleHorseNotes: SaleHorseNoteModel[] =
      study?.saleHorse?.saleHorseNotes;

    // First, let's split up the studyFile collections.
    studyFiles.forEach((studyFile) => {
      if (studyFile.studyFileMimeTypeId === 1) {
        mp4StudyFiles.push(studyFile);
      } else if (studyFile.studyFileMimeTypeId === 2) {
        pdfStudyFiles.push(studyFile);
      } else if (studyFile.studyFileMimeTypeId === 3) {
        xrayStudyFiles.push(studyFile);
      }
    });
    const pdfGroups = groupByUpdateDateAndStatusId(pdfStudyFiles);
    const mp4Groups = groupByUpdateDateAndStatusId(mp4StudyFiles);
    const imageGroups = groupByUpdateDateAndStatusId(xrayStudyFiles);
    const mp4StudyFileItems: {
      studyFileIds: number[];
      studyFile: StudyFile;
    }[] = [];
    mp4Groups.forEach((items) => {
      items.forEach((item) => {
        const model = {
          studyFileIds: item.map((sf) => sf.studyFileId),
          studyFile: item[0],
        };

        mp4StudyFileItems.push(model);
      });
    });
    const pdfStudyFileItems: {
      studyFileIds: number[];
      studyFile: StudyFile;
    }[] = [];
    pdfGroups.forEach((items) => {
      items.forEach((item) => {
        const model = {
          studyFileIds: item.map((sf) => sf.studyFileId),
          studyFile: item[0],
        };

        pdfStudyFileItems.push(model);
      });
    });

    const xrayStudyFileItems: {
      studyFileIds: number[];
      studyFile: StudyFile;
    }[] = [];
    imageGroups.forEach((items) => {
      items.forEach((item) => {
        const model = {
          studyFileIds: item.map((sf) => sf.studyFileId),
          studyFile: item[0],
        };

        xrayStudyFileItems.push(model);
      });
    });

    return {
      mp4StudyFileItems,
      mp4StudyFiles,
      pdfStudyFileItems,
      pdfStudyFiles,
      xrayStudyFileItems,
      xrayStudyFiles,
      saleHorseNotes,
    };
  });

function groupByUpdateDateAndStatusId(
  array: StudyFile[]
): Map<string, Map<number, StudyFile[]>> {
  const resultMap = new Map();

  for (const item of array) {
    const { updatedDate, studyStatusId, memberId } = item;
    const key = `${updatedDate}_${memberId}`;

    if (!resultMap.has(key)) {
      resultMap.set(key, new Map());
    }

    const dateMap = resultMap.get(key);

    if (!dateMap.has(studyStatusId)) {
      dateMap.set(studyStatusId, []);
    }

    const statusArray = dateMap.get(studyStatusId);
    statusArray.push(item);
  }

  return resultMap;
}
export const selectStartedActivityLogId = createSelector(
  selectState,
  (state: StudyState) => state.startedActivityLogId
);
export const selectIsActivityEnded = createSelector(
  selectState,
  (state: StudyState) => state.isActivityEnded
);
export const selectStudiesSearch = createSelector(
  selectState,
  (state: StudyState) => state.studiesSearch
);
export const selectStudiesSearchDisplay = createSelector(
  selectState,
  (state: StudyState) => state.studiesDisplayData
);

export const selectStudiesSearchLoaded = createSelector(
  selectState,
  (state: StudyState) => state.isStudiesSearchLoaded
);

export const selectStudyFileMimeTypesLoaded = createSelector(
  selectState,
  (state: StudyState) => state.isStudyMimeTypesLoaded
);
export const selectStudyFileMimeTypes = createSelector(
  selectState,
  (state: StudyState) => state.studyFileMimeTypes
);

export const selectStudyFileDocTypesUpdated = createSelector(
  selectState,
  (state: StudyState) => state.isStudyFileDocTypesUpdated
);

export const selectStudyDescriptionUpdated = createSelector(
  selectState,
  (state: StudyState) => state.isStudyDescriptionUpdated
);

export const selectStudyFileDocTypesLoaded = createSelector(
  selectState,
  (state: StudyState) => state.isStudyDocTypesLoaded
);
export const selectStudyFileDocTypes = createSelector(
  selectState,
  (state: StudyState) => state.studyFileDocTypes
);
export const selectStudySaleSubmitted = createSelector(
  selectState,
  (state: StudyState) => state.isStudySaleSubmitted
);
export const selectStudySaleSubmittedErrors = createSelector(
  selectState,
  (state: StudyState) => state.studySaleSubmittedErrors
);

export const selectStudyPdfFiles = createSelector(
  selectState,
  (state: StudyState) => {
    return state.studyPdfFiles;
  }
);

export const cancelStudyAndDeleteFilesError = createSelector(
  selectState,
  (state: StudyState) => state.cancelStudyAndDeleteFilesError
);

export const cancelStudyAndDeleteFilesSuccess = createSelector(
  selectState,
  (state: StudyState) => state.cancelStudyAndDeleteFilesSuccess
);

export const cancelStudySubmissionError = createSelector(
  selectState,
  (state: StudyState) => state.cancelStudySubmissionError
);

export const cancelStudySubmissionSuccess = createSelector(
  selectState,
  (state: StudyState) => state.cancelStudySubmissionSuccess
);

export const setStudyActiveStatusError = createSelector(
  selectState,
  (state: StudyState) => state.setStudyActiveStatusError
);

export const setStudyActiveStatusSucess = createSelector(
  selectState,
  (state: StudyState) => state.setStudyActiveStatusSucess
);

// Upload study creen only
export const selectSubmitStudyValidationMessages = createSelector(
  selectStudyFileMap(true),
  selectStudyFileMap(false),
  selectSaleSettings,
  selectMediaCategories,
  (studyFileMap, allStudyFileMap, saleSettings, requiredMediaCategories) => {
    // saleSubmission is keyed (SaleId, HorseAgeCategoryId), NOT one row per sale.
    // [0] is safe here only because the studies store loads settings from the
    // horse-scoped endpoint (/api/Sales/{saleId}/{horseId}/settings), which
    // filters to this horse's age category server-side (SaleService.cs:274).
    const saleSubmissionInfo = saleSettings?.saleSubmission[0];

    let validationMesages: SubmissionValidationIssue[] = [];
    if (!saleSettings.isScopeAllowedWithoutXrays && allStudyFileMap.xrayAllStudyFiles.length == 0) {
      validationMesages.push({ title: `You must submit at least one X-Ray.` });
    }

    // Opt-in protocol enforcement. Only bites when the sales company has set
    // this age category to Complete; NULL and Partial keep the old behaviour,
    // so no existing sale changes until someone turns it on.
    //
    // Deliberately NOT an early return or an else-if: a study can fail the
    // protocol AND be missing a scope video at the same time, and the vet needs
    // to be told both at once rather than discovering them one submit at a time.
    if (
      saleSubmissionInfo?.xrayCompleteType === XrayCompleteType.Complete &&
      requiredMediaCategories?.length
    ) {
      // xrayAllStudyFiles is already the right coverage set: it excludes denied
      // files and additional images, and spans draft + submitted + approved, so
      // a vet topping up an earlier submission is credited for what they sent
      // before rather than being asked for the whole protocol again.
      const coveredCategoryIds = new Set(
        allStudyFileMap.xrayAllStudyFiles
          .filter((sf) => sf.mediaCategoryId != null)
          .map((sf) => sf.mediaCategoryId)
      );

      const missingCategories = requiredMediaCategories.filter(
        (mc) => !coveredCategoryIds.has(mc.id)
      );

      if (missingCategories.length) {
        // Report every required group, not just the short ones, so the vet can
        // see the whole protocol and tick it off rather than re-submitting to
        // discover the next gap.
        const groupTotals = new Map<string, { required: number; covered: number }>();
        requiredMediaCategories.forEach((mc) => {
          const groupName = mc.mediaGroupName || 'Other';
          const tally = groupTotals.get(groupName) ?? { required: 0, covered: 0 };
          tally.required += 1;
          if (coveredCategoryIds.has(mc.id)) {
            tally.covered += 1;
          }
          groupTotals.set(groupName, tally);
        });

        const details = Array.from(groupTotals.entries())
          .filter(([, tally]) => tally.covered < tally.required)
          .map(
            ([groupName, tally]) =>
              `${groupName}: ${tally.covered} of ${tally.required}`
          );

        validationMesages.push({
          title:
            `This sale requires the complete X-Ray protocol. ` +
            `${requiredMediaCategories.length - missingCategories.length} of ` +
            `${requiredMediaCategories.length} required images have been provided, ` +
            `${missingCategories.length} still missing.`,
          details,
        });
      }
    }
    if (!allStudyFileMap.mp4StudyFiles.length) {
      if (saleSubmissionInfo.isScope && !studyFileMap.mp4StudyFiles.length) {
        validationMesages.push({ title: `You must submit at least one scope video.` });
      }
    }

    if (!allStudyFileMap.pdfStudyFiles.length) {
      if (
        (saleSubmissionInfo.scopeReport || saleSubmissionInfo.isXRayReport) &&
        !studyFileMap.pdfStudyFiles.length
      ) {
        validationMesages.push({ title: `You must submit at least one scope report.` });
      }
    }

    if (studyFileMap.pdfStudyFiles.length) {
      const hasMissingDocType = studyFileMap.pdfStudyFiles.find(
        (sf) => !sf.documentTypeId
      );

      if (hasMissingDocType) {
        validationMesages.push({
          title: `You must select a document type for all reports.`,
        });
      }
    }

    if (saleSubmissionInfo.isXRayReport) {
      const hasXrayReport = studyFileMap.pdfStudyFiles.find(
        (sf) => sf.documentTypeId === 110
      );

      const submissionXrayReport = allStudyFileMap.pdfStudyFiles.find(
        (sf) => sf.documentTypeId === 110
      );

      if (!hasXrayReport && !submissionXrayReport) {
        validationMesages.push({
          title: `You must include an x-ray report in the submission.`,
        });
      }
    }

    if (saleSubmissionInfo.scopeReport) {
      const hasScopeReport = studyFileMap.pdfStudyFiles.find(
        (sf) => sf.documentTypeId === 120
      );

      const submissionScopeReport = allStudyFileMap.pdfStudyFiles.find(
        (sf) => sf.documentTypeId === 120
      );

      if (!hasScopeReport && !submissionScopeReport) {
        validationMesages.push({
          title: `You must include a scope report in the submission.`,
        });
      }
    }

    if (studyFileMap.mp4StudyFiles.length) {
      const hasMissingDocType = studyFileMap.mp4StudyFiles.find(
        (sf) => !sf.documentTypeId
      );

      if (hasMissingDocType) {
        validationMesages.push({ title: `You must select a video type for all videos.` });
      }
    }

    //if (studyFileMap.multipleStudyFiles.length) {
    //  validationMesages.push(
    //    `There are ${studyFileMap.multipleStudyFiles.length} study files with the same category. Please review the submission.`
    //  );
    //}

    if (studyFileMap.unmatchedStudyFiles.length) {
      validationMesages.push({
        title: `There are ${studyFileMap.unmatchedStudyFiles.length} unmatched study files, please review the submission.`,
      });
    }

    const unsbumittedStudyFiles = studyFileMap.study?.studyFiles.filter(
      (sf) => sf.studyStatusId < 2
    );
    if (
      unsbumittedStudyFiles === null ||
      typeof unsbumittedStudyFiles === 'undefined' ||
      unsbumittedStudyFiles.length === 0
    ) {
      validationMesages.push({
        title: 'No new files to submit, all files have been submitted previously.',
      });
    }

    if (validationMesages.length === 0) {
      return null;
    }
    return validationMesages;
  }
);

export const selectMergeSuccess = createSelector(
  selectState,
  (state: StudyState) => state.mergeSuccess
);

export const selectMergeError = createSelector(
  selectState,
  (state: StudyState) => state.error
);

export const selectOnCompleteBatchSuccess = createSelector(
  selectState,
  (state: StudyState) => state.completeBatchSuccess
);

export const selectOnCompleteBatchError = createSelector(
  selectState,
  (state: StudyState) => state.errorCompleteBatch
);

export const selectStudyFileDelete = createSelector(
  selectState,
  (state: StudyState) => state.isStudyFileDeleted
);
export const selectStudyFileError = createSelector(
  selectState,
  (state: StudyState) => state.deleteStudyFileError
);

export const selectShareStudySuccess = createSelector(
  selectState,
  (state: StudyState) => state.shareSuccess
);

export const selectShareStudyError = createSelector(
  selectState,
  (state: StudyState) => state.shareError
);

export const selectShareStudyUID = createSelector(
  selectState,
  (state: StudyState) => state.studyShareUID
);

export const selectActivitPerformed = createSelector(
  selectState,
  (state: StudyState) => state.isActivityPerform
);
